/**
 * AccountStatus — friendly landing page shown when an org is suspended or
 * terminated. The 423 interceptor stashes the detail in sessionStorage and
 * redirects the user here so they see a clear "what's wrong + who to contact"
 * message instead of a broken dashboard.
 *
 * Used by /app/frontend/src/contexts/AuthContext.js
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ShieldAlert, ShieldOff, Mail, LogOut } from 'lucide-react';

export default function AccountStatus() {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('pg_org_status_info');
      if (raw) setInfo(JSON.parse(raw));
    } catch (_e) { /* ignore */ }
  }, []);

  const logout = () => {
    localStorage.removeItem('pg_token');
    localStorage.removeItem('pg_refresh_token');
    sessionStorage.removeItem('pg_org_status_info');
    navigate('/login');
  };

  // Default fallback if user lands here directly without context
  const code = info?.code || 'org_suspended';
  const orgName = info?.org_name || 'Your organization';
  const reason = info?.reason || 'Your account is currently unavailable.';
  const contact = info?.contact_email || 'support@pipeguide.tech';
  const deletionAt = info?.deletion_scheduled_for ? new Date(info.deletion_scheduled_for) : null;

  const isSuspended = code === 'org_suspended';
  const Icon = isSuspended ? ShieldAlert : ShieldOff;
  const titleColor = isSuspended ? 'text-amber-700' : 'text-red-700';
  const accentBg = isSuspended ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';
  const title = isSuspended ? 'Account Paused' : 'Account Closed';

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4" data-testid="account-status-page">
      <Card className="max-w-lg w-full border-zinc-200 shadow-sm">
        <CardContent className="pt-8 pb-6 px-8 text-center">
          <div className={`w-16 h-16 rounded-full ${accentBg} border-2 flex items-center justify-center mx-auto mb-5`}>
            <Icon className={`w-8 h-8 ${titleColor}`} />
          </div>
          <h1 className={`text-2xl font-bold ${titleColor} mb-2`} data-testid="account-status-title">
            {title}
          </h1>
          <p className="text-sm text-zinc-600 mb-1">
            <span className="font-semibold text-zinc-900">{orgName}</span>
          </p>
          <p className="text-sm text-zinc-600 mb-6" data-testid="account-status-reason">
            {reason}
          </p>

          {deletionAt && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs text-red-800 mb-5">
              Your data is scheduled for permanent deletion on{' '}
              <span className="font-semibold">{deletionAt.toLocaleDateString()}</span>.
              Contact us before then if you need to export anything.
            </div>
          )}

          <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4 text-sm text-zinc-700 mb-6 text-left">
            <div className="font-semibold text-zinc-900 mb-2">What now?</div>
            <ul className="space-y-1.5 text-xs">
              <li>• Reach out to <a href={`mailto:${contact}`} className="text-brand-primary font-semibold underline">{contact}</a></li>
              {isSuspended ? (
                <>
                  <li>• Resolve any outstanding billing or compliance items with us</li>
                  <li>• We&apos;ll reactivate the account as soon as it&apos;s resolved</li>
                </>
              ) : (
                <>
                  <li>• Request a data export if you need a copy of your transcripts</li>
                  <li>• If this was unexpected, we can restore the account while data is still retained</li>
                </>
              )}
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              onClick={() => window.location.href = `mailto:${contact}?subject=${encodeURIComponent(`Re: ${orgName} — ${title}`)}`}
              className="bg-brand-primary hover:bg-brand-primary/90 text-white rounded-md"
              data-testid="account-status-contact"
            >
              <Mail className="w-4 h-4 mr-2" /> Contact billing
            </Button>
            <Button
              variant="outline"
              onClick={logout}
              className="rounded-md"
              data-testid="account-status-logout"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
